/**
 * Product Hub — pricing.
 *
 * Two pure functions and the vocabulary around them. Both are here rather than in the backend
 * because the price a screen displays and the price the API computes have to be the same number,
 * and the only way to guarantee that is one implementation.
 *
 * ## The rule this file exists to enforce
 *
 * **Nothing in this module ever converts a currency.** The spec is explicit about it three times
 * (§14, §18, §44), and it is not a simplification to be lifted later — a converted price is a
 * number nobody entered, changing daily, that a shop would be contractually bound to. A store in
 * USD does not inherit an ILS base price at some rate; it simply has no price until somebody
 * enters one, and `source: 'missing'` is how that is reported.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/pricing`.
 */
import { roundMoney } from './payment.types';
import { PhOption, phOptions } from './productHub.core';
import { applyPercent, toMoney } from './productHub.money';

/* ------------------------------------------------------------------ */
/* Effective price                                                     */
/* ------------------------------------------------------------------ */

/**
 * Where the price a store shows came from.
 *
 * `missing` is a first-class answer, not an error. It means "this variant has no price in this
 * store", which is exactly what a variant priced only in ILS looks like from a USD store — and
 * the pricelist screen has to draw it as an empty cell asking to be filled, not as a failure.
 */
export const PRICE_SOURCES = ['store_price', 'base_price', 'missing'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

export const PRICE_SOURCE_LABELS: Record<PriceSource, string> = {
  store_price: 'מחיר ייעודי לחנות',
  base_price: 'יורש ממחיר הבסיס',
  missing: 'אין מחיר'
};

export const PRICE_SOURCE_OPTIONS: PhOption<PriceSource>[] = phOptions(PRICE_SOURCES, PRICE_SOURCE_LABELS);

/** What a store charges for a variant, and why. The shape §53 requires for every resolved value. */
export interface EffectivePrice {
  price: number | null;
  /** The "was" price, for a sale. Only ever comes from a `StorePrice`; a base price has none. */
  compareAtPrice: number | null;
  /** Always the **store's** currency — never the base price's, because nothing is converted. */
  currency: string;
  source: PriceSource;
  sourceLabel: string;
  /** The base price, for the UI's "central value" hint. Null when it is in another currency. */
  basePrice: number | null;
  /** Present when a `StorePrice` row exists, for the optimistic-locking round trip. */
  version: number | null;
}

/**
 * An `EffectivePrice` for a whole **product** in a store, rather than for one variant.
 *
 * Here rather than in either app's type file because both declare it: the backend builds it in
 * `productStorePrices.service.ts` and two admin screens read it. Two declarations of one payload is
 * how a field gets added on one side and silently ignored on the other.
 *
 * A product is priced through its **default** variant (ADR-001) — the one a simple product presents
 * as its own, and the one the wizard writes when somebody types a price beside a store. The two
 * counts are what stop that single number from being read as the whole answer for a product with
 * several variants.
 */
export interface ProductStorePrice extends EffectivePrice {
  /** The variant the number belongs to — what an edit against this row writes to. */
  variantId: number | null;
  /** Live variants this store gives a price of its own. */
  pricedVariantCount: number;
  /** Live variants the product has, so "1 of 3 priced" is sayable. */
  variantCount: number;
}

export interface StorePriceInput {
  price: string | number | null;
  compareAtPrice?: string | number | null;
  currency: string;
  version: number;
}

export interface BasePriceInput {
  basePrice: string | number | null;
  baseCurrency: string;
}

/**
 * The single decision point for "what does this store charge?".
 *
 * Order, and the reason for each step:
 *
 *  1. **A `StorePrice` wins.** It was entered for this store deliberately.
 *  2. **Otherwise the base price — but only if the currencies match.** This is the whole rule.
 *     An ILS base price is not 3.7× a USD one; it is simply not a price in USD.
 *  3. **Otherwise `missing`.**
 *
 * A `StorePrice` in the wrong currency is treated as absent rather than trusted. The invariant
 * "a store price is in its store's currency" cannot be expressed as a database constraint (it
 * spans two tables), so it is enforced on write — and this is the read-side backstop for a row
 * that got in some other way.
 */
export const resolveEffectivePrice = (
  storeCurrency: string,
  storePrice: StorePriceInput | null | undefined,
  base: BasePriceInput | null | undefined
): EffectivePrice => {
  const baseAmount = base && base.baseCurrency === storeCurrency ? toMoney(base.basePrice) : null;

  const answer = (source: PriceSource, price: number | null, extra: Partial<EffectivePrice> = {}): EffectivePrice => ({
    price,
    compareAtPrice: null,
    currency: storeCurrency,
    source,
    sourceLabel: PRICE_SOURCE_LABELS[source],
    basePrice: baseAmount,
    version: null,
    ...extra
  });

  if (storePrice && storePrice.currency === storeCurrency) {
    const price = toMoney(storePrice.price);
    // A row that exists with a null price is not a price. It is a half-filled cell, and treating
    // it as free would be the most expensive rounding error in the module.
    if (price !== null) {
      return answer('store_price', price, {
        compareAtPrice: toMoney(storePrice.compareAtPrice ?? null),
        version: storePrice.version
      });
    }
  }

  if (baseAmount !== null) return answer('base_price', baseAmount, { version: storePrice?.version ?? null });

  return answer('missing', null, { version: storePrice?.version ?? null });
};

/* ------------------------------------------------------------------ */
/* Bulk operations                                                     */
/* ------------------------------------------------------------------ */

/** What the pricelist's selection bar can do to a set of prices. */
export const BULK_PRICE_OPERATIONS = ['set', 'increase_percent', 'decrease_percent', 'return_to_base'] as const;
export type BulkPriceOperation = (typeof BULK_PRICE_OPERATIONS)[number];

export const BULK_PRICE_OPERATION_LABELS: Record<BulkPriceOperation, string> = {
  set: 'קבע מחיר',
  increase_percent: 'העלה באחוזים',
  decrease_percent: 'הפחת באחוזים',
  return_to_base: 'חזור למחיר בסיס'
};

export const BULK_PRICE_OPERATION_OPTIONS = phOptions(BULK_PRICE_OPERATIONS, BULK_PRICE_OPERATION_LABELS);

/**
 * The new price for one row of a bulk operation — or `null` meaning "delete the store price".
 *
 * `return_to_base` deletes rather than writing the base value in. Copying it would silently stop
 * the price following the product, which is the same mistake the override columns exist to avoid;
 * and the row would then look like a deliberate store price to everybody who read it afterwards.
 *
 * The percentage operations work on the **effective** price, so a variant currently inheriting its
 * base price gets a store price at base+10% rather than being skipped. That is what "raise these
 * forty by ten percent" means to the person who asked for it.
 */
export const applyBulkPriceOperation = (
  operation: BulkPriceOperation,
  current: EffectivePrice,
  amount: number
): number | null => {
  switch (operation) {
    case 'set':
      return roundMoney(amount);

    case 'increase_percent':
      // Nothing to raise by a percentage of. Skipped rather than turned into `amount`, which
      // would quietly price an unpriced variant off a number that meant "percent".
      return current.price === null ? null : applyPercent(current.price, amount);

    case 'decrease_percent':
      if (current.price === null) return null;
      // Clamped at zero: a 120% discount is a data-entry mistake, and a negative price would
      // propagate into every total downstream before anybody noticed.
      return Math.max(0, applyPercent(current.price, -amount));

    case 'return_to_base':
      return null;
  }
};

/** True when the operation removes the store price rather than writing one. */
export const isPriceRemoval = (operation: BulkPriceOperation): boolean => operation === 'return_to_base';
